import { useState, useEffect, useRef } from "react";
import { supabase } from "./supabase";

const C = {
  bg:"#f8f5f0", surface:"#ffffff", card:"#ffffff", border:"#e8e4df",
  borderGold:"rgba(180,140,60,0.3)", goldBg:"rgba(180,140,60,0.08)",
  gold:"#b48c3c", goldD:"#8a6f2e",
  green:"#2e8b57", greenL:"rgba(46,139,87,0.12)",
  red:"#c0392b", redL:"rgba(192,57,43,0.10)",
  blue:"#2563eb", blueL:"rgba(37,99,235,0.10)",
  orange:"#d97706", orangeL:"rgba(217,119,6,0.10)",
  purple:"#7c3aed", purpleL:"rgba(124,58,237,0.10)",
  text:"#1a1a2e", muted:"#6b7280", dim:"#9ca3af", light:"#f3f4f6",
};
const F = "'Montserrat',sans-serif";
const S = "'Playfair Display','Cormorant Garamond',Georgia,serif";

const inp = {
  width:"100%", padding:"10px 12px", background:C.light,
  border:"1px solid "+C.border, borderRadius:8, fontSize:13,
  color:C.text, fontFamily:F, outline:"none", boxSizing:"border-box",
};

function Btn({ children, v="primary", sz="md", full, onClick, disabled, style }) {
  const variants = {
    primary: { bg:`linear-gradient(135deg,${C.gold},${C.goldD})`, color:"#fff", border:"none" },
    sec:     { bg:C.surface, color:C.gold, border:`1px solid ${C.borderGold}` },
    ghost:   { bg:"transparent", color:C.muted, border:`1px solid ${C.border}` },
    danger:  { bg:C.redL, color:C.red, border:`1px solid ${C.red}33` },
    success: { bg:C.greenL, color:C.green, border:`1px solid ${C.green}33` },
  };
  const sizes = { sm:"6px 10px", md:"9px 16px", lg:"12px 22px" };
  const fontS = { sm:11, md:12, lg:13 };
  const v_ = variants[v];
  return (
    <button onClick={onClick} disabled={disabled} style={{
      padding:sizes[sz], fontSize:fontS[sz], fontWeight:700,
      ...v_, borderRadius:8, cursor:disabled?"not-allowed":"pointer",
      fontFamily:F, transition:"all 0.15s", opacity:disabled?0.5:1,
      width:full?"100%":"auto", letterSpacing:"0.02em", ...style,
    }}>{children}</button>
  );
}

function Card({ children, style }) {
  return <div style={{ background:C.card, border:`1px solid ${C.border}`, borderRadius:12, ...style }}>{children}</div>;
}

const CATEGORY_LABELS = {
  booking_rules: { label:"Booking Rules", color:C.blue, bg:C.blueL, emoji:"📅" },
  pricing_rules: { label:"Pricing", color:C.green, bg:C.greenL, emoji:"💰" },
  sales_strategy: { label:"Sales", color:C.purple, bg:C.purpleL, emoji:"🎯" },
  tone_voice: { label:"Tone", color:C.orange, bg:C.orangeL, emoji:"💬" },
  policies: { label:"Policies", color:C.red, bg:C.redL, emoji:"📋" },
  treatments_info: { label:"Treatments", color:C.gold, bg:C.goldBg, emoji:"💆" },
  general: { label:"General", color:C.muted, bg:C.light, emoji:"📌" },
};

export default function BotTraining() {
  const [view, setView] = useState("split");
  return (
    <div style={{ background:C.bg, fontFamily:F }}>
      <div style={{ padding:"18px 32px", borderBottom:`1px solid ${C.border}`, background:C.surface, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontSize:20, fontWeight:800, color:C.text, fontFamily:S }}>🎓 Bot Training</div>
          <div style={{ fontSize:11, color:C.muted, marginTop:2 }}>
            Train your AI receptionist with natural language. Test it in real time.
          </div>
        </div>
        <div style={{ display:"flex", gap:6 }}>
          <Btn v={view==="split"?"primary":"ghost"} sz="sm" onClick={() => setView("split")}>⬛⬛ Split</Btn>
          <Btn v={view==="manager"?"primary":"ghost"} sz="sm" onClick={() => setView("manager")}>👨‍💼 Manager</Btn>
          <Btn v={view==="test"?"primary":"ghost"} sz="sm" onClick={() => setView("test")}>🧪 Test</Btn>
          <Btn v={view==="rules"?"primary":"ghost"} sz="sm" onClick={() => setView("rules")}>📋 Rules</Btn>
        </div>
      </div>

      {view === "split" && (
        <div style={{ display:"grid", gridTemplateColumns:"repeat(auto-fit, minmax(320px, 1fr))", gap:16, minHeight:"calc(100dvh - 320px)", maxWidth:1400, margin:"0 auto", padding:"0 20px" }}>
          <div style={{ borderRight:`1px solid ${C.border}` }}><ManagerChat compact /></div>
          <div><TestChat compact /></div>
        </div>
      )}
      {view === "manager" && <ManagerChat />}
      {view === "test" && <TestChat />}
      {view === "rules" && <RulesManager />}
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// MANAGER CHAT - You give instructions, manager creates rules
// ═══════════════════════════════════════════════════════════════════
function ManagerChat({ compact }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const scrollRef = useRef();

  const load = async () => {
    const { data } = await supabase.from("bot_manager_messages")
      .select("*").order("created_at", { ascending: true }).limit(100);
    setMessages(data || []);
  };

  useEffect(() => {
    load();
    const ch = supabase.channel("manager-chat")
      .on("postgres_changes", { event: "*", schema: "public", table: "bot_manager_messages" }, load)
      .on("postgres_changes", { event: "*", schema: "public", table: "bot_rules" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    if (!input.trim() || loading) return;
    setLoading(true);
    const msg = input;
    setInput("");
    try {
      await supabase.functions.invoke("bot-manager", { body: { message: msg } });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
    load();
  };

  const approveRule = async (ruleId) => {
    await supabase.functions.invoke("bot-manager", { body: { action: "approve_rule", rule_id: ruleId } });
    load();
  };

  const rejectRule = async (ruleId) => {
    await supabase.functions.invoke("bot-manager", { body: { action: "reject_rule", rule_id: ruleId } });
    load();
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height: compact ? "100%" : "calc(100vh - 320px)", maxWidth: compact ? "100%" : 700, margin: compact ? "0" : "0 auto", border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden", background:C.surface }}>
      <div style={{ padding:"12px 20px", background:C.surface, borderBottom:`1px solid ${C.border}` }}>
        <div style={{ fontSize:14, fontWeight:800, color:C.text, fontFamily:S }}>👨‍💼 Manager Chat</div>
        <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>
          Tell the bot what to do. Examples: "Always 10 min gap between bookings" · "Never quote Botox prices over SMS"
        </div>
      </div>

      <div ref={scrollRef} style={{ flex:1, overflow:"auto", minHeight:0, padding:"16px 20px", background:C.bg }}>
        {messages.length === 0 && (
          <div style={{ padding:32, textAlign:"center", color:C.muted, fontSize:12 }}>
            <div style={{ fontSize:28, marginBottom:8 }}>👋</div>
            <div style={{ fontWeight:700, color:C.text, marginBottom:4 }}>Train your bot in plain English</div>
            <div>Try: "Always confirm with a YES before booking" or "Don't mention prices for injectables"</div>
          </div>
        )}
        {messages.map(m => (
          <ManagerMessage key={m.id} msg={m} onApprove={approveRule} onReject={rejectRule} />
        ))}
        {loading && (
          <div style={{ padding:"8px 14px", color:C.muted, fontSize:12, fontStyle:"italic" }}>
            🤔 Manager is processing...
          </div>
        )}
      </div>

      <div style={{ padding:"12px 16px", background:C.surface, borderTop:`1px solid ${C.border}`, display:"flex", gap:8 }}>
        <input style={inp} value={input} onChange={e => setInput(e.target.value)}
               placeholder="Type instruction..." onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
               disabled={loading} />
        <Btn onClick={send} disabled={loading || !input.trim()}>
          {loading ? "..." : "Send"}
        </Btn>
      </div>
    </div>
  );
}

function ManagerMessage({ msg, onApprove, onReject }) {
  const [proposedRule, setProposedRule] = useState(null);

  useEffect(() => {
    if (msg.proposed_rule_id) {
      supabase.from("bot_rules").select("*").eq("id", msg.proposed_rule_id).single()
        .then(({ data }) => setProposedRule(data));
    }
  }, [msg.proposed_rule_id]);

  if (msg.role === "user") {
    return (
      <div style={{ display:"flex", justifyContent:"flex-end", marginBottom:10 }}>
        <div style={{ maxWidth:"75%" }}>
          <div style={{
            padding:"10px 14px",
            background:`linear-gradient(135deg,${C.gold},${C.goldD})`,
            color:"#fff", borderRadius:14, fontSize:13, lineHeight:1.45,
          }}>{msg.message_text}</div>
          <div style={{ fontSize:9, color:C.dim, marginTop:3, textAlign:"right" }}>
            {new Date(msg.created_at).toLocaleTimeString()}
          </div>
        </div>
      </div>
    );
  }

  return (
    <div style={{ display:"flex", justifyContent:"flex-start", marginBottom:10 }}>
      <div style={{ maxWidth:"80%" }}>
        <div style={{
          padding:"10px 14px",
          background:C.surface, color:C.text,
          border:`1px solid ${C.border}`,
          borderRadius:14, fontSize:13, lineHeight:1.45,
        }}>
          <div>{msg.message_text}</div>

          {proposedRule && !proposedRule.is_active && (
            <div style={{
              marginTop:10, padding:10,
              background: CATEGORY_LABELS[proposedRule.category]?.bg || C.light,
              borderRadius:8, fontSize:11,
            }}>
              <div style={{ display:"flex", alignItems:"center", gap:6, marginBottom:6 }}>
                <span style={{ fontSize:14 }}>{CATEGORY_LABELS[proposedRule.category]?.emoji}</span>
                <span style={{ fontWeight:700, color:CATEGORY_LABELS[proposedRule.category]?.color, textTransform:"uppercase", fontSize:10, letterSpacing:"0.05em" }}>
                  {CATEGORY_LABELS[proposedRule.category]?.label}
                </span>
                <span style={{ fontSize:10, color:C.muted, marginLeft:"auto" }}>Priority: {proposedRule.priority}</span>
              </div>
              <div style={{ fontWeight:700, marginBottom:4, color:C.text }}>{proposedRule.title}</div>
              <div style={{ color:C.text, marginBottom:8, lineHeight:1.4 }}>{proposedRule.instruction}</div>
              <div style={{ display:"flex", gap:6 }}>
                <Btn v="success" sz="sm" onClick={() => onApprove(proposedRule.id)}>✓ Apply Rule</Btn>
                <Btn v="danger" sz="sm" onClick={() => onReject(proposedRule.id)}>✗ Reject</Btn>
              </div>
            </div>
          )}

          {proposedRule?.is_active && (
            <div style={{ marginTop:8, padding:"6px 10px", background:C.greenL, borderRadius:6, fontSize:11, color:C.green, fontWeight:700 }}>
              ✓ Rule applied — bot will follow this from now on
            </div>
          )}
        </div>
        <div style={{ fontSize:9, color:C.dim, marginTop:3 }}>
          🤖 Manager · {new Date(msg.created_at).toLocaleTimeString()}
        </div>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// TEST CHAT - You pretend to be a customer, bot responds like SMS
// ═══════════════════════════════════════════════════════════════════
function TestChat({ compact }) {
  const [messages, setMessages] = useState([]);
  const [input, setInput] = useState("");
  const [loading, setLoading] = useState(false);
  const [dryRun, setDryRun] = useState(true);
  const [sessionId, setSessionId] = useState(null);
  const [clients, setClients] = useState([]);
  const [asClientId, setAsClientId] = useState(null);
  const scrollRef = useRef();

  useEffect(() => {
    let sid = sessionStorage.getItem("bot_test_session_id");
    if (!sid) {
      sid = `test_${Date.now()}_${Math.random().toString(36).slice(2, 8)}`;
      sessionStorage.setItem("bot_test_session_id", sid);
    }
    setSessionId(sid);
  }, []);

  // Load client list for dropdown
  useEffect(() => {
    const loadClients = async () => {
      const all = []; let from = 0; const PAGE = 1000;
      while (true) {
        const { data: chunk } = await supabase.from("clients")
          .select("id,name,email,phone").order("id", { ascending: false }).range(from, from + PAGE - 1);
        if (!chunk || !chunk.length) break;
        all.push(...chunk);
        if (chunk.length < PAGE) break;
        from += PAGE;
      }
      setClients(all);
    };
    loadClients();
  }, []);

  useEffect(() => {
    if (sessionId) load();
  }, [sessionId]);

  const load = async () => {
    if (!sessionId) return;
    const { data } = await supabase.from("bot_test_messages")
      .select("*").eq("session_id", sessionId).order("created_at", { ascending: true });
    setMessages(data || []);
  };

  useEffect(() => {
    if (scrollRef.current) scrollRef.current.scrollTop = scrollRef.current.scrollHeight;
  }, [messages.length]);

  const send = async () => {
    if (!input.trim() || loading || !sessionId) return;
    setLoading(true);
    const msg = input;
    setInput("");
    try {
      await supabase.functions.invoke("bot-test-chat", {
        body: { session_id: sessionId, message: msg, dry_run: dryRun, as_client_id: asClientId }
      });
    } catch (e) {
      console.error(e);
    }
    setLoading(false);
    load();
  };

  const reset = async () => {
    if (!sessionId) return;
    if (!window.confirm("Clear test conversation?")) return;
    await supabase.functions.invoke("bot-test-chat", {
      body: { session_id: sessionId, reset: true }
    });
    setMessages([]);
  };

  const cleanup = async () => {
    if (!window.confirm("Delete all [TEST] clients and appointments? This cannot be undone.")) return;
    const { data } = await supabase.functions.invoke("bot-test-chat", {
      body: { session_id: sessionId, cleanup_test_data: true }
    });
    if (data?.success) {
      window.alert(`Cleaned: ${data.cleaned?.appointments || 0} appointments, ${data.cleaned?.clients || 0} clients`);
      setMessages([]);
    } else {
      window.alert("Cleanup failed - check console");
    }
  };

  return (
    <div style={{ display:"flex", flexDirection:"column", height: compact ? "100%" : "calc(100vh - 320px)", maxWidth: compact ? "100%" : 700, margin: compact ? "0" : "0 auto", border:`1px solid ${C.border}`, borderRadius:12, overflow:"hidden", background:C.surface }}>
      {!dryRun && (
        <div style={{ background:C.redL, borderBottom:`2px solid ${C.red}`, padding:"8px 20px", display:"flex", alignItems:"center", gap:10 }}>
          <span style={{ fontSize:16 }}>⚠️</span>
          <div style={{ fontSize:11, color:C.red, fontWeight:700 }}>
            LIVE MODE — Real clients and appointments will be created in the system (tagged as [TEST] for cleanup)
          </div>
        </div>
      )}
      <div style={{ padding:"10px 20px", background:C.goldBg, borderBottom:`1px solid ${C.border}`, display:"flex", alignItems:"center", gap:10 }}>
        <span style={{ fontSize:11, fontWeight:700, color:C.gold, textTransform:"uppercase", letterSpacing:"0.05em" }}>
          Test as:
        </span>
        <select
          value={asClientId || ""}
          onChange={e => setAsClientId(e.target.value ? Number(e.target.value) : null)}
          style={{ ...inp, flex:1, maxWidth:400, padding:"6px 10px", fontSize:12 }}
        >
          <option value="">— New / Anonymous customer —</option>
          {clients.map(c => (
            <option key={c.id} value={c.id}>
              {c.name || "(no name)"} {c.email ? `· ${c.email}` : ""} {c.phone ? `· ${c.phone.slice(-10)}` : ""}
            </option>
          ))}
        </select>
        <span style={{ fontSize:10, color:C.muted, fontStyle:"italic" }}>
          {asClientId ? "Bot will recognize this client" : "Bot treats as new customer"}
        </span>
      </div>
      <div style={{ padding:"12px 20px", background:C.surface, borderBottom:`1px solid ${C.border}`, display:"flex", justifyContent:"space-between", alignItems:"center" }}>
        <div>
          <div style={{ fontSize:14, fontWeight:800, color:C.text, fontFamily:S }}>🧪 Test Chat</div>
          <div style={{ fontSize:10, color:C.muted, marginTop:2 }}>
            Pretend you're a customer. Bot uses your training rules.
          </div>
        </div>
        <div style={{ display:"flex", gap:8, alignItems:"center" }}>
          {/* Mode toggle */}
          <div style={{ display:"flex", border:`1px solid ${C.border}`, borderRadius:8, overflow:"hidden", fontSize:10, fontWeight:700 }}>
            <button onClick={() => setDryRun(true)} style={{
              padding:"5px 10px", border:"none", cursor:"pointer", fontFamily:F,
              background: dryRun ? `linear-gradient(135deg,${C.gold},${C.goldD})` : "transparent",
              color: dryRun ? "#fff" : C.muted,
            }}>🧪 Dry Run</button>
            <button onClick={() => setDryRun(false)} style={{
              padding:"5px 10px", border:"none", cursor:"pointer", fontFamily:F,
              background: !dryRun ? C.red : "transparent",
              color: !dryRun ? "#fff" : C.muted,
            }}>🔴 LIVE</button>
          </div>
          <Btn v="ghost" sz="sm" onClick={reset}>🔄 Reset chat</Btn>
          <Btn v="danger" sz="sm" onClick={cleanup}>🗑️ Cleanup TEST data</Btn>
        </div>
      </div>

      <div ref={scrollRef} style={{ flex:1, overflow:"auto", minHeight:0, padding:"16px 20px", background:C.bg }}>
        {messages.length === 0 && (
          <div style={{ padding:32, textAlign:"center", color:C.muted, fontSize:12 }}>
            <div style={{ fontSize:28, marginBottom:8 }}>📱</div>
            <div style={{ fontWeight:700, color:C.text, marginBottom:4 }}>Try messaging the bot</div>
            <div>Like: "Hi I want to book a facial" or "what are your hours?"</div>
            {!dryRun && (
              <div style={{ marginTop:12, padding:8, background:C.redL, borderRadius:6, color:C.red, fontWeight:700, fontSize:11 }}>
                LIVE MODE: Bookings & clients will be created in the system
              </div>
            )}
          </div>
        )}
        {messages.map(m => (
          <div key={m.id} style={{
            display:"flex",
            justifyContent: m.role === "user" ? "flex-end" : "flex-start",
            marginBottom:10,
          }}>
            <div style={{ maxWidth:"75%" }}>
              <div style={{
                padding:"10px 14px",
                background: m.role === "user" ? C.blue : C.surface,
                color: m.role === "user" ? "#fff" : C.text,
                border: m.role === "user" ? "none" : `1px solid ${C.border}`,
                borderRadius:14, fontSize:13, lineHeight:1.45, whiteSpace:"pre-wrap",
              }}>
                {m.message_text}
              </div>
              <div style={{ fontSize:9, color:C.dim, marginTop:3, textAlign: m.role === "user" ? "right" : "left" }}>
                {m.role === "user" ? "👤 You (as customer)" : "🤖 Bot"} · {new Date(m.created_at).toLocaleTimeString()}
                {m.ai_tools_used?.length > 0 && (
                  <span style={{ marginLeft:6, color:C.purple, fontWeight:700 }}>
                    🛠 {m.ai_tools_used.map(t => t.name).join(", ")}
                  </span>
                )}
              </div>
            </div>
          </div>
        ))}
        {loading && (
          <div style={{ padding:"8px 14px", color:C.muted, fontSize:12, fontStyle:"italic" }}>
            🤖 Bot is typing...
          </div>
        )}
      </div>

      <div style={{ padding:"12px 16px", background:C.surface, borderTop:`1px solid ${C.border}`, display:"flex", gap:8 }}>
        <input style={inp} value={input} onChange={e => setInput(e.target.value)}
               placeholder="Type as customer..." onKeyDown={e => e.key === "Enter" && !e.shiftKey && send()}
               disabled={loading} />
        <Btn onClick={send} disabled={loading || !input.trim()}>
          {loading ? "..." : "Send"}
        </Btn>
      </div>
    </div>
  );
}

// ═══════════════════════════════════════════════════════════════════
// RULES MANAGER - View, edit, toggle, delete rules
// ═══════════════════════════════════════════════════════════════════
function RulesManager() {
  const [rules, setRules] = useState([]);
  const [filter, setFilter] = useState("all");
  const [editing, setEditing] = useState(null);

  const load = async () => {
    const { data } = await supabase.from("bot_rules")
      .select("*").order("priority", { ascending: false }).order("created_at", { ascending: false });
    setRules(data || []);
  };

  useEffect(() => {
    load();
    const ch = supabase.channel("rules")
      .on("postgres_changes", { event: "*", schema: "public", table: "bot_rules" }, load)
      .subscribe();
    return () => { supabase.removeChannel(ch); };
  }, []);

  const toggle = async (rule) => {
    await supabase.functions.invoke("bot-manager", {
      body: { action: "toggle_rule", rule_id: rule.id, is_active: !rule.is_active }
    });
    load();
  };

  const remove = async (rule) => {
    if (!window.confirm(`Delete rule "${rule.title}"?`)) return;
    await supabase.functions.invoke("bot-manager", {
      body: { action: "delete_rule", rule_id: rule.id }
    });
    load();
  };

  const filtered = filter === "all" ? rules
    : filter === "active" ? rules.filter(r => r.is_active)
    : filter === "pending" ? rules.filter(r => !r.is_active)
    : rules.filter(r => r.category === filter);

  const counts = {
    all: rules.length,
    active: rules.filter(r => r.is_active).length,
    pending: rules.filter(r => !r.is_active).length,
  };

  return (
    <div style={{ padding:24 }}>
      <div style={{ display:"flex", gap:6, marginBottom:16, flexWrap:"wrap" }}>
        {[
          { id:"all", label:"All", count: counts.all },
          { id:"active", label:"Active", count: counts.active, color:C.green },
          { id:"pending", label:"Pending Approval", count: counts.pending, color:C.orange },
          ...Object.keys(CATEGORY_LABELS).map(k => ({
            id: k, label: CATEGORY_LABELS[k].label, count: rules.filter(r => r.category === k).length
          }))
        ].map(f => f.count > 0 && (
          <button key={f.id} onClick={() => setFilter(f.id)} style={{
            padding:"6px 12px", fontSize:11, fontWeight:700,
            color: filter === f.id ? "#fff" : (f.color || C.muted),
            background: filter === f.id ? (f.color || C.gold) : C.surface,
            border:`1px solid ${filter === f.id ? (f.color || C.gold) : C.border}`,
            borderRadius:8, cursor:"pointer", fontFamily:F,
          }}>{f.label} ({f.count})</button>
        ))}
      </div>

      {filtered.length === 0 ? (
        <Card style={{ padding:32, textAlign:"center", color:C.muted, fontSize:13 }}>
          No rules in this filter. Go to Manager Chat and start training your bot!
        </Card>
      ) : (
        <div style={{ display:"grid", gap:10 }}>
          {filtered.map(r => (
            <RuleCard key={r.id} rule={r} onToggle={toggle} onDelete={remove} onEdit={() => setEditing(r)} />
          ))}
        </div>
      )}

      {editing && (
        <RuleEditor rule={editing} onClose={() => setEditing(null)} onSave={load} />
      )}
    </div>
  );
}

function RuleCard({ rule, onToggle, onDelete, onEdit }) {
  const cat = CATEGORY_LABELS[rule.category] || CATEGORY_LABELS.general;
  return (
    <Card style={{ padding:16, opacity: rule.is_active ? 1 : 0.65 }}>
      <div style={{ display:"flex", justifyContent:"space-between", alignItems:"flex-start", gap:12 }}>
        <div style={{ flex:1 }}>
          <div style={{ display:"flex", alignItems:"center", gap:8, marginBottom:6 }}>
            <span style={{ fontSize:16 }}>{cat.emoji}</span>
            <span style={{ fontSize:10, fontWeight:700, color:cat.color, textTransform:"uppercase", letterSpacing:"0.05em", padding:"2px 8px", background:cat.bg, borderRadius:6 }}>
              {cat.label}
            </span>
            <span style={{ fontSize:10, color:C.muted }}>Priority: {rule.priority}</span>
            {!rule.is_active && (
              <span style={{ fontSize:10, color:C.orange, fontWeight:700, padding:"2px 6px", background:C.orangeL, borderRadius:4 }}>
                PENDING APPROVAL
              </span>
            )}
            {rule.is_active && (
              <span style={{ fontSize:10, color:C.green, fontWeight:700 }}>
                ● ACTIVE
              </span>
            )}
          </div>
          <div style={{ fontSize:13, fontWeight:700, color:C.text, marginBottom:4 }}>{rule.title}</div>
          <div style={{ fontSize:12, color:C.text, lineHeight:1.45 }}>{rule.instruction}</div>
          {rule.source_message && (
            <div style={{ fontSize:10, color:C.dim, marginTop:6, fontStyle:"italic" }}>
              Original: "{rule.source_message}"
            </div>
          )}
        </div>
        <div style={{ display:"flex", flexDirection:"column", gap:6 }}>
          <Btn v={rule.is_active ? "success" : "primary"} sz="sm" onClick={() => onToggle(rule)}>
            {rule.is_active ? "✓ Active" : "Enable"}
          </Btn>
          <Btn v="ghost" sz="sm" onClick={onEdit}>Edit</Btn>
          <Btn v="danger" sz="sm" onClick={() => onDelete(rule)}>Delete</Btn>
        </div>
      </div>
    </Card>
  );
}

function RuleEditor({ rule, onClose, onSave }) {
  const [r, setR] = useState({ ...rule });
  const [saving, setSaving] = useState(false);

  const save = async () => {
    setSaving(true);
    await supabase.functions.invoke("bot-manager", {
      body: {
        action: "edit_rule",
        rule_id: r.id,
        title: r.title,
        instruction: r.instruction,
        category: r.category,
        priority: parseInt(r.priority) || 50,
      }
    });
    setSaving(false);
    onClose();
    onSave();
  };

  return (
    <div style={{ position:"fixed", inset:0, background:"rgba(0,0,0,0.5)", zIndex:1000, display:"flex", alignItems:"center", justifyContent:"center", padding:20 }}>
      <Card style={{ padding:24, width:560, maxHeight:"90vh", overflow:"auto" }}>
        <div style={{ fontSize:18, fontWeight:800, color:C.text, fontFamily:S, marginBottom:16 }}>Edit Rule</div>
        <div style={{ display:"flex", flexDirection:"column", gap:12 }}>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:C.gold, textTransform:"uppercase" }}>Category</label>
            <select style={inp} value={r.category} onChange={e => setR({ ...r, category: e.target.value })}>
              {Object.keys(CATEGORY_LABELS).map(k => (
                <option key={k} value={k}>{CATEGORY_LABELS[k].label}</option>
              ))}
            </select>
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:C.gold, textTransform:"uppercase" }}>Title</label>
            <input style={inp} value={r.title || ""} onChange={e => setR({ ...r, title: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:C.gold, textTransform:"uppercase" }}>Instruction</label>
            <textarea style={{ ...inp, minHeight:100, resize:"vertical" }} value={r.instruction} onChange={e => setR({ ...r, instruction: e.target.value })} />
          </div>
          <div>
            <label style={{ fontSize:11, fontWeight:700, color:C.gold, textTransform:"uppercase" }}>Priority (1-100)</label>
            <input style={inp} type="number" min="1" max="100" value={r.priority} onChange={e => setR({ ...r, priority: e.target.value })} />
          </div>
        </div>
        <div style={{ display:"flex", gap:8, justifyContent:"flex-end", marginTop:18 }}>
          <Btn v="ghost" onClick={onClose}>Cancel</Btn>
          <Btn onClick={save} disabled={saving}>{saving ? "Saving..." : "Save Changes"}</Btn>
        </div>
      </Card>
    </div>
  );
}
